import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { arrayMove } from '@dnd-kit/sortable'

import { useFetch } from '@reveni-io/reveni-ui'

import useEmailTemplatesList from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/hooks/useEmailTemplatesList/useEmailTemplatesList'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import routes from '@/Router/routes.constants'

import { servicesByConfigurationMode } from './constants'
import { getEmailTemplatesDisplayPayload } from './helpers'

const useEmailTemplatesConfiguration = ({
  setToastInfo,
  navigate,
  location,
  configurationMode,
  merchantId,
  returnPortalId,
  isUnifiedDashboard,
}) => {
  const { t } = useTranslation()
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })

  const {
    emailTemplates: accumulatedResults,
    setEmailTemplates: setAccumulatedResults,
    loading,
    enabledPortalsWithGlobal,
    refetch: refetchEmailTemplates,
  } = useEmailTemplatesList({ configurationMode, merchantId, returnPortalId })

  const [hasLocalChanges, setHasLocalChanges] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [templateToDelete, setTemplateToDelete] = useState(null)

  const { fetchData: deleteEmailTemplateService } = useFetch(services.deleteEmailTemplate, {
    doInitialRequest: false,
  })
  const { fetchData: duplicateEmailTemplateService } = useFetch(services.duplicateEmailTemplate, {
    doInitialRequest: false,
  })
  const { fetchData: editEmailTemplatesDisplay, loading: savingDisplay } = useFetch(
    services.editEmailTemplatesDisplay,
    { doInitialRequest: false }
  )

  const refetchAll = () => {
    setHasLocalChanges(false)
    refetchEmailTemplates()
  }

  const onSearch = query => {
    setSearchQuery(query.toLowerCase())
  }

  const getFilteredResults = () => {
    if (!searchQuery) return accumulatedResults

    return accumulatedResults.filter(template => {
      const name = template.name?.toLowerCase() || ''
      const description = template.description?.toLowerCase() || ''

      return name.includes(searchQuery) || description.includes(searchQuery)
    })
  }

  const getTemplateById = templateId => {
    return accumulatedResults.find(template => template.id === templateId)
  }

  const isTemplateEnabled = templateId => {
    return getTemplateById(templateId)?.is_enabled || false
  }

  const onToggleTemplate = (templateId, checked) => {
    setAccumulatedResults(prev => prev.map(item => (item.id === templateId ? { ...item, is_enabled: checked } : item)))
    setHasLocalChanges(true)
  }

  const onEdit = template => {
    const route = isUnifiedDashboard
      ? routes.merchantConfigurationPage.emailTemplateDetail
      : routes.configurationPage.emailTemplateDetail

    navigate(route.replace(':id', template.id), {
      state: {
        childPageTitle: template.name,
        emailType: template.category,
        previousLocation: location,
      },
    })
  }

  const onCreate = () => {
    const route = isUnifiedDashboard
      ? routes.merchantConfigurationPage.emailTemplateDetail
      : routes.configurationPage.emailTemplateDetail

    navigate(route.replace(':id', 'new'), {
      state: {
        childPageTitle: t('Crear plantilla'),
        previousLocation: location,
      },
    })
  }

  const onRequestDelete = templateId => {
    setTemplateToDelete(templateId)
  }

  const onConfirmDelete = async () => {
    try {
      await deleteEmailTemplateService({ ...entityId, templateId: templateToDelete })
      setAccumulatedResults(prev => prev.filter(item => item.id !== templateToDelete))
      setToastInfo({ show: true, message: t('Plantilla eliminada correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage || t('Error al eliminar la plantilla'), type: 'error' })
    }
    setTemplateToDelete(null)
  }

  const onCancelDelete = () => {
    setTemplateToDelete(null)
  }

  const onDuplicate = async templateId => {
    try {
      await duplicateEmailTemplateService({ ...entityId, templateId })
      setToastInfo({ show: true, message: t('Plantilla duplicada correctamente'), type: 'success' })
      refetchAll()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage || t('Error al duplicar la plantilla'), type: 'error' })
    }
  }

  const setEmailTemplates = updater => {
    setAccumulatedResults(prev => updater(prev))
    setHasLocalChanges(true)
  }

  const onSortEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = accumulatedResults.findIndex(r => r.id === active.id)
    const newIndex = accumulatedResults.findIndex(r => r.id === over.id)

    setAccumulatedResults(prev => [...arrayMove(prev, oldIndex, newIndex)])
    setHasLocalChanges(true)
    setDragging(false)
    document.body.style.cursor = 'inherit'
  }

  const onSortStart = () => {
    setDragging(true)
    document.body.style.cursor = 'grabbing'
  }

  const onSaveDisplay = async () => {
    try {
      await editEmailTemplatesDisplay({
        ...entityId,
        emailTemplates: getEmailTemplatesDisplayPayload(accumulatedResults),
      })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      setHasLocalChanges(false)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    refetchAll()
  }

  return {
    emailTemplates: getFilteredResults(),
    setEmailTemplates,
    loading,
    isTemplateEnabled,
    onToggleTemplate,
    getTemplateById,
    onEdit,
    onCreate,
    onRequestDelete,
    onConfirmDelete,
    onCancelDelete,
    onDuplicate,
    showDeleteModal: !!templateToDelete,
    totalTemplates: accumulatedResults.length,
    enabledPortalsWithGlobal,
    hasLocalChanges,
    onSaveDisplay,
    savingDisplay,
    revertChanges,
    dragging,
    onSortEnd,
    onSortStart,
    onSearch,
  }
}

export default useEmailTemplatesConfiguration
